import { DataTablePaginate } from '@/common/ui/table/data-table-paginate'
import { PaginatedSmsAutoTransactionLogsResponse } from '../../types'
import { createColumns } from './constants'
import { Button } from '@/common/ui/button'
import { Icons } from '@/common/ui/icons'
import { RefetchOptions } from '@tanstack/react-query'
import { Input } from '@/common/ui/input'
import { useState } from 'react'
import { useQueryState } from 'nuqs'
import RematchDialog from '../RematchDialog'
import CancelDialog from '../CancelDialog'

interface SmsAutoLogsTableProps {
	data: PaginatedSmsAutoTransactionLogsResponse['data']
	total: number
	onRefresh: (options?: RefetchOptions) => Promise<unknown>
	isLoading?: boolean
}

export default function SmsAutoLogsTable({
	data,
	total,
	onRefresh,
	isLoading,
}: SmsAutoLogsTableProps) {
	const [localAccountNumber, setLocalAccountNumber] = useState('')
	const [localAmount, setLocalAmount] = useState('')

	const [accountNumber, setAccountNumber] = useQueryState('accountNumber')
	const [amount, setAmount] = useQueryState('amount')

	const [rematchTarget, setRematchTarget] = useState<{
		id: string
		amount: number
		accountNumber: string
		message: string | null
		gatewayMinDeposit?: number | null
	} | null>(null)
	const [cancelTarget, setCancelTarget] = useState<string | null>(null)

	const columns = createColumns(
		(id, smsAmount, accountNumber, message, gatewayMinDeposit) =>
			setRematchTarget({
				id,
				amount: smsAmount,
				accountNumber,
				message,
				gatewayMinDeposit,
			}),
		(id) => setCancelTarget(id),
	)

	const handleSearch = () => {
		setAccountNumber(localAccountNumber || null)
		setAmount(localAmount || null)
	}

	const handleClear = () => {
		setLocalAccountNumber('')
		setLocalAmount('')
		setAccountNumber(null)
		setAmount(null)
	}

	const hasFilters = accountNumber || amount

	return (
		<div className="space-y-4">
			{/* Search Controls */}
			<div className="space-y-4">
				<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
					<Input
						placeholder="ค้นหาเลขบัญชี"
						value={localAccountNumber}
						onChange={(e) => setLocalAccountNumber(e.target.value)}
						className="h-8 w-full text-sm sm:h-9 sm:text-base"
						onKeyDown={(e) => {
							if (e.key === 'Enter') handleSearch()
						}}
					/>
					<Input
						placeholder="ค้นหาจำนวนเงิน"
						type="number"
						value={localAmount}
						onChange={(e) => setLocalAmount(e.target.value)}
						className="h-8 w-full text-sm sm:h-9 sm:text-base"
						onKeyDown={(e) => {
							if (e.key === 'Enter') handleSearch()
						}}
					/>
				</div>

				<div className="flex gap-2">
					<Button
						onClick={handleSearch}
						size="sm"
						className="h-8 px-3 text-xs sm:h-9 sm:px-4 sm:text-sm"
					>
						ค้นหา
					</Button>
					{hasFilters && (
						<Button
							variant="outline"
							size="sm"
							onClick={handleClear}
							className="h-8 px-3 text-xs sm:h-9 sm:px-4 sm:text-sm"
						>
							ล้าง
						</Button>
					)}
					<Button
						variant="outline"
						size="sm"
						onClick={() => onRefresh()}
						title="รีเฟรชข้อมูล"
						className="h-8 w-8 sm:h-9 sm:w-9"
					>
						<Icons.refresh className="h-4 w-4" />
					</Button>
				</div>
			</div>

			{/* Table */}
			<DataTablePaginate
				columns={columns}
				data={data}
				totalItems={total}
				isLoading={isLoading}
			/>

			{rematchTarget && (
				<RematchDialog
					smsLogId={rematchTarget.id}
					smsAmount={rematchTarget.amount}
					smsAccountNumber={rematchTarget.accountNumber}
					smsMessage={rematchTarget.message}
					gatewayMinDeposit={rematchTarget.gatewayMinDeposit ?? undefined}
					isOpen={!!rematchTarget}
					onClose={() => setRematchTarget(null)}
				/>
			)}
			{cancelTarget && (
				<CancelDialog
					smsLogId={cancelTarget}
					isOpen={!!cancelTarget}
					onClose={() => setCancelTarget(null)}
				/>
			)}
		</div>
	)
}
